import { useState, useEffect, useCallback } from 'react'
import { AlertTriangle, RefreshCw, UserCheck, Pin, Clock, AlertCircle, SlidersHorizontal, X, Search, Loader2 } from 'lucide-react'
import PriorityBadge from '@/components/rr/PriorityBadge'
import BucketBadge from '@/components/rr/BucketBadge'
import { rrGetQueue, rrGetAgents, rrAssignFromQueue } from '@/actions/rr'

type Priority = 'Very High' | 'High' | 'Medium' | 'Low'
type Bucket = 'ABC' | 'Reorder' | 'RTO'
type ReasonType = 'cap' | 'sticky' | 'hours' | 'noagent'

interface QueueItem {
  id: number; uuid: string; lead_id: number
  name: string; phone: string; product: string
  bucket: Bucket; bucket_raw: string
  priority: Priority; priority_raw: string
  waiting: string; reason: string; reason_type: ReasonType; attempts: number
}

interface Agent { user_id: number; name: string; email: string }

const AV_COLORS = ['#2f9e6b','#3b82c4','#b9772e','#8a5cd1','#c2557a','#2aa1a8']
function avColor(name: string) { let h = 0; for (const c of name) h = (h * 31 + c.charCodeAt(0)) % AV_COLORS.length; return AV_COLORS[h] }
function initials(n: string) { const p = n.trim().split(' '); return ((p[0]?.[0]??'')+(p[1]?.[0]??'')).toUpperCase() }

function ReasonIcon({ type }: { type: ReasonType }) {
  const cfg = {
    sticky:  { bg: 'bg-blue-50',   text: 'text-blue-600',  Icon: Pin },
    hours:   { bg: 'bg-amber-50',  text: 'text-amber-600', Icon: Clock },
    noagent: { bg: 'bg-red-50',    text: 'text-red-500',   Icon: AlertCircle },
    cap:     { bg: 'bg-gray-100',  text: 'text-gray-500',  Icon: SlidersHorizontal },
  }[type]
  return <span className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${cfg.bg} ${cfg.text}`}><cfg.Icon className="w-3.5 h-3.5" /></span>
}

type TabKey = 'All' | Bucket
const TABS: TabKey[] = ['All', 'ABC', 'Reorder', 'RTO']

function AssignModal({ lead, onClose, onAssigned }: { lead: QueueItem; onClose: () => void; onAssigned: () => void }) {
  const [agents, setAgents] = useState<Agent[]>([])
  const [selected, setSelected] = useState<number | null>(null)
  const [agentSearch, setAgentSearch] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    rrGetAgents().then(res => {
      const list: Agent[] = (res?.data ?? []) as Agent[]
      setAgents(list)
      if (list.length) setSelected(list[0].user_id)
    })
  }, [])

  async function handleAssign() {
    if (!selected) return
    setSaving(true)
    try {
      await rrAssignFromQueue(lead.id, selected)
      onAssigned()
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="flex items-start justify-between p-5 pb-3">
          <div>
            <h3 className="text-lg font-extrabold text-gray-900">Assign lead</h3>
            <p className="text-xs text-gray-500 mt-1">Pick an agent to manually assign this queued lead.</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500"><X className="w-4 h-4" /></button>
        </div>
        <div className="mx-5 flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-xl">
          <span className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ background: avColor(lead.name) }}>{initials(lead.name)}</span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-extrabold text-gray-900">{lead.name}</div>
            <div className="text-xs text-gray-400">{lead.phone} · {lead.product}</div>
          </div>
          <PriorityBadge priority={lead.priority} />
        </div>
        <div className="px-5 pt-3 pb-1">
          <div className="flex items-center gap-2 h-9 border border-gray-300 rounded-lg px-3 text-gray-400">
            <Search className="w-4 h-4 flex-shrink-0" />
            <input value={agentSearch} onChange={e => setAgentSearch(e.target.value)} placeholder="Search agents…" className="flex-1 text-sm text-gray-900 border-0 outline-none bg-transparent" />
          </div>
        </div>
        <div className="px-5 py-2 max-h-52 overflow-y-auto space-y-1">
          {agents.filter(a => !agentSearch || a.name.toLowerCase().includes(agentSearch.toLowerCase()) || a.email.toLowerCase().includes(agentSearch.toLowerCase())).map(a => (
            <label key={a.user_id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${selected === a.user_id ? 'bg-green-50 border-green-200' : 'border-transparent hover:bg-gray-50'}`}>
              <input type="radio" name="agent" checked={selected === a.user_id} onChange={() => setSelected(a.user_id)} className="w-4 h-4 accent-brandColor" />
              <span className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0" style={{ background: avColor(a.name) }}>{initials(a.name)}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-900">{a.name}</div>
                <div className="text-xs text-gray-400">{a.email}</div>
              </div>
            </label>
          ))}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 bg-gray-50/60">
          <button onClick={onClose} className="h-9 px-4 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50">Cancel</button>
          <button onClick={handleAssign} disabled={!selected || saving} className="h-9 px-4 bg-brandColor text-white rounded-lg text-sm font-semibold hover:bg-brand-hover inline-flex items-center gap-2 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />} Assign
          </button>
        </div>
      </div>
    </div>
  )
}

export default function QueuePage() {
  const [queue, setQueue] = useState<QueueItem[]>([])
  const [tab, setTab] = useState<TabKey>('All')
  const [assignLead, setAssignLead] = useState<QueueItem | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(() => {
    setLoading(true)
    const bucketParam = tab !== 'All' ? { ABC: 'abandoned_cart', Reorder: 'reorder', RTO: 'rto' }[tab] : undefined
    rrGetQueue({ bucket: bucketParam, limit: 200 }).then(res => {
      setQueue((res?.data ?? []) as QueueItem[])
    }).finally(() => setLoading(false))
  }, [tab])

  useEffect(() => { load() }, [load])

  const counts: Record<TabKey, number> = { All: queue.length, ABC: 0, Reorder: 0, RTO: 0 }
  for (const q of queue) counts[q.bucket] = (counts[q.bucket] || 0) + 1

  const filtered = tab === 'All' ? queue : queue.filter(q => q.bucket === tab)

  return (
    <div className="h-full flex flex-col p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-extrabold text-gray-900">Assignment Queue</h1>
          <p className="text-sm text-gray-500 mt-0.5">Leads waiting to be assigned — sorted by priority.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 h-9 px-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} className={`inline-flex items-center gap-2 h-9 px-4 rounded-xl text-sm font-semibold transition-colors ${t === tab ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            {t}
            <span className={`text-xs font-extrabold px-1.5 py-0.5 rounded-full ${t === tab ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>{counts[t]}</span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm flex-1 flex flex-col overflow-hidden">
        {loading
          ? <div className="flex justify-center items-center h-48"><Loader2 className="w-8 h-8 animate-spin text-gray-300" /></div>
          : filtered.length === 0
            ? <div className="flex flex-col items-center justify-center h-48 text-gray-400"><AlertTriangle className="w-8 h-8 mb-3 text-gray-200" /><p className="font-semibold">Queue is empty</p><p className="text-sm mt-1">All leads have been assigned.</p></div>
            : <>
              <div className="grid grid-cols-[1fr_140px_120px_100px_1fr_160px] gap-0 px-5 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-400 border-b border-gray-100 bg-gray-50 flex-shrink-0">
                <span>Lead</span><span>Product</span><span>Bucket</span><span>Priority</span><span>Reason</span><span>Actions</span>
              </div>
              <div className="divide-y divide-gray-50 overflow-y-auto flex-1">
                {filtered.map(q => (
                  <div key={q.id} className="grid grid-cols-[1fr_140px_120px_100px_1fr_160px] gap-0 px-5 py-3.5 items-center hover:bg-gray-50/60">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ background: avColor(q.name) }}>{initials(q.name)}</span>
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-gray-900 truncate">{q.name}</div>
                        <div className="text-xs text-gray-400">{q.phone}</div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-700 pr-2 leading-tight">{q.product || '—'}</div>
                    <div><BucketBadge bucket={q.bucket} /></div>
                    <div><PriorityBadge priority={q.priority} /></div>
                    <div className="flex items-center gap-2 min-w-0">
                      <ReasonIcon type={q.reason_type} />
                      <div className="min-w-0">
                        <div className="text-xs text-gray-600 truncate">{q.reason}</div>
                        <div className="text-[10px] text-gray-400">waiting {q.waiting} · {q.attempts} attempts</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setAssignLead(q)} className="inline-flex items-center gap-1.5 h-8 px-3 bg-brandColor text-white rounded-lg text-xs font-semibold hover:bg-brand-hover">
                        <UserCheck className="w-3.5 h-3.5" /> Assign
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
        }
      </div>

      {assignLead && <AssignModal lead={assignLead} onClose={() => setAssignLead(null)} onAssigned={load} />}
    </div>
  )
}
